import React, { useState } from 'react';
import {
  Animated,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { BRAND } from 'images/RelicVault';
import RelicCard from './RelicCard';
import CompanionCard from './CompanionCard';
import {
  CloseIcon,
  CoinIcon,
  NoGuardianIcon,
  RefreshIcon,
  SparkleIcon,
  TorchFlameIcon,
  VaultChestIcon,
  VaultCornerIcon,
} from '../shared/RelicVaultIcons';
import { useOneShot, useTorchFlicker } from '../shared/useRelicVaultAnimations';
import { gridItemWidth, useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import {
  COLORS,
  bodyFont,
  em,
  headingFont,
  hoverTransition,
  panelSurface,
} from '../shared/theme';
import { COMPANIONS, COMPANION_NONE_ID } from 'config/relicVault/companions';
import { getRelic } from 'utils/relicVault/relicDisplay';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/**
 * The Relic Vault Store ("Vaulty" design): 5 relic slots with up to two paid
 * refreshes a day, and the 9 guardian companions.
 */
const StoreView = ({
  vault,
  balance,
  refreshAllowance,
  purchase,
  onRefresh,
  onOpenVault,
  onEquip,
  onClose,
}) => {
  const { isTablet, isPhone } = useRelicVaultLayout();
  const [relicGridWidth, setRelicGridWidth] = useState(0);
  const [companionGridWidth, setCompanionGridWidth] = useState(0);

  const relicColumns = isPhone ? 2 : isTablet ? 3 : 5;
  const relicGap = isPhone ? 10 : isTablet ? 14 : 18;
  const companionColumns = isPhone ? 1 : isTablet ? 2 : 3;
  const companionGap = isPhone ? 12 : isTablet ? 14 : 18;

  const vaultCount = vault.ownedRelicIds.length + vault.ownedCompanionIds.length;
  const refreshAvailable = refreshAllowance.left > 0;
  const refreshDisabled = !refreshAvailable || balance < refreshAllowance.nextCost;
  const storeRelics = vault.storeRelicIds.map(getRelic).filter(Boolean);
  const noGuardian = vault.equippedCompanionId === COMPANION_NONE_ID;

  return (
    <View
      style={[
        styles.content,
        isTablet && styles.contentTablet,
        isPhone && styles.contentPhone,
      ]}>
      {/* Top bar */}
      <View style={[styles.topbar, isPhone && styles.topbarPhone]}>
        <View style={[styles.logoRow, isPhone && styles.logoRowPhone]}>
          <Image
            source={BRAND.badge}
            style={[
              styles.logoBadge,
              webOnly({ filter: 'drop-shadow(0 0 12px rgba(212,232,92,0.4))' }),
            ]}
          />
          <Text style={[styles.logoWord, isPhone && styles.logoWordPhone]}>Quiklearn</Text>
        </View>
        <View style={styles.statRow}>
          <View style={[styles.pill, isPhone && styles.pillPhone]}>
            <CoinIcon size={18} />
            <Text style={styles.coinBalance}>{balance} QuikCoins</Text>
          </View>
          <VaultPill count={vaultCount} isPhone={isPhone} onPress={onOpenVault} />
          {!isPhone && <CloseButton onPress={onClose} />}
        </View>
        {/* On phones the pills fill their row, so close is pinned to the top bar's corner. */}
        {isPhone && <CloseButton onPress={onClose} pinned />}
      </View>

      {/* Hero */}
      <View style={[styles.hero, isPhone && styles.heroPhone]}>
        <View style={styles.titleRow}>
          <SparkleIcon />
          <Text
            style={[
              styles.heroTitle,
              isTablet && styles.heroTitleTablet,
              isPhone && styles.heroTitlePhone,
            ]}>
            Guild Relic Vault
          </Text>
          <SparkleIcon />
        </View>
        <Text style={styles.heroSub}>Collect. Personalize. Keep learning.</Text>
      </View>

      {/* Relic Vault Store panel */}
      <View style={[styles.panel, styles.vaultPanel, isPhone && styles.panelPhone]}>
        <Image
          source={BRAND.vaultPanelTexture}
          resizeMode="cover"
          style={[StyleSheet.absoluteFill, styles.panelTexture]}
        />
        <View
          pointerEvents="none"
          style={[
            StyleSheet.absoluteFill,
            styles.panelTextureShade,
            webOnly({
              backgroundImage:
                'linear-gradient(180deg, rgba(10,8,6,0.12) 0%, rgba(10,8,6,0.4) 100%)',
            }),
          ]}
        />
        <VaultCorners />

        <View style={styles.panelHead}>
          <View style={styles.titleRow}>
            <Torch />
            <Text style={styles.panelTitle}>Relic Vault Store</Text>
            <Torch />
          </View>
        </View>

        <View
          style={styles.grid}
          onLayout={e => setRelicGridWidth(e.nativeEvent.layout.width)}>
          {relicGridWidth > 0 &&
            storeRelics.map((relic, index) => (
              <View
                key={relic.id}
                style={{
                  marginRight: (index + 1) % relicColumns === 0 ? 0 : relicGap,
                  marginBottom: relicGap,
                }}>
                <RelicCard
                  relic={relic}
                  owned={vault.ownedRelicIds.includes(relic.id)}
                  canAfford={balance >= relic.price}
                  width={gridItemWidth(relicGridWidth, relicColumns, relicGap)}
                  compact={isPhone}
                  onSelect={purchase.openRelic}
                />
              </View>
            ))}
        </View>

        <View style={[styles.refreshRow, isPhone && styles.refreshRowPhone]}>
          <View style={[styles.refreshNote, isPhone && styles.refreshItemPhone]}>
            <RefreshIcon />
            <Text style={styles.refreshNoteText}>
              {refreshAvailable ? 'Refreshes reset daily' : 'New relics tomorrow'}
            </Text>
          </View>
          <RefreshButton
            available={refreshAvailable}
            disabled={refreshDisabled}
            left={refreshAllowance.left}
            max={refreshAllowance.max}
            cost={refreshAllowance.nextCost}
            isPhone={isPhone}
            onPress={onRefresh}
          />
          <VaultChestButton pulse={purchase.vaultPulse} isPhone={isPhone} onPress={onOpenVault} />
        </View>
      </View>

      {/* Guardian panel */}
      <View style={[styles.panel, styles.guardianPanel, isPhone && styles.panelPhone]}>
        <View style={styles.panelHead}>
          <View style={styles.titleRow}>
            <SparkleIcon small />
            <Text style={styles.panelTitle}>Choose Your Guardian</Text>
            <SparkleIcon small />
          </View>
          <Text style={styles.panelSub}>A companion for your learning journey</Text>
          <NoGuardianButton active={noGuardian} onPress={() => onEquip(COMPANION_NONE_ID)} />
        </View>

        <View
          // Cancel the last row's bottom gap so the panel padding matches the design.
          style={[styles.grid, isPhone && styles.companionGridPhone, { marginBottom: -companionGap }]}
          onLayout={e => setCompanionGridWidth(e.nativeEvent.layout.width)}>
          {companionGridWidth > 0 &&
            COMPANIONS.map((companion, index) => (
              <View
                key={companion.id}
                style={{
                  marginRight: (index + 1) % companionColumns === 0 ? 0 : companionGap,
                  marginBottom: companionGap,
                }}>
                <CompanionCard
                  companion={companion}
                  owned={vault.ownedCompanionIds.includes(companion.guildId)}
                  equipped={vault.equippedCompanionId === companion.guildId}
                  canAfford={balance >= companion.price}
                  balance={balance}
                  width={gridItemWidth(companionGridWidth, companionColumns, companionGap)}
                  onSelect={purchase.openCompanion}
                  onEquip={onEquip}
                />
              </View>
            ))}
        </View>
      </View>
    </View>
  );
};

const CloseButton = ({ onPress, pinned }) => (
  <TouchableOpacity
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel="Close store"
    style={[styles.closeBtn, pinned && styles.closeBtnPinned]}>
    <CloseIcon size={14} />
  </TouchableOpacity>
);

const Torch = () => {
  const flicker = useTorchFlicker();
  return (
    <Animated.View style={[flicker, webOnly({ transformOrigin: '50% 90%' })]}>
      <TorchFlameIcon />
    </Animated.View>
  );
};

/** Ornamental gold corners on the vault panel, mirrored per corner. */
const VaultCorners = () => (
  <>
    <View pointerEvents="none" style={[styles.corner, { top: 12, left: 12 }]}>
      <VaultCornerIcon />
    </View>
    <View
      pointerEvents="none"
      style={[styles.corner, { top: 12, right: 12, transform: [{ scaleX: -1 }] }]}>
      <VaultCornerIcon />
    </View>
    <View
      pointerEvents="none"
      style={[styles.corner, { bottom: 12, left: 12, transform: [{ scaleY: -1 }] }]}>
      <VaultCornerIcon />
    </View>
    <View
      pointerEvents="none"
      style={[
        styles.corner,
        { bottom: 12, right: 12, transform: [{ scaleX: -1 }, { scaleY: -1 }] },
      ]}>
      <VaultCornerIcon />
    </View>
  </>
);

const VaultPill = ({ count, isPhone, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={`Open My Vault, ${count} items`}
      style={[styles.pill, isPhone && styles.pillPhone, styles.pillSpacing]}>
      <Text style={[styles.vaultPillText, hovered && { color: '#FFFFFF' }]}>
        My Vault {'·'} {count} items
      </Text>
    </TouchableOpacity>
  );
};

const RefreshButton = ({ available, disabled, left, max, cost, isPhone, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      disabled={disabled}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      style={[
        styles.refreshBtn,
        isPhone ? styles.refreshItemPhone : styles.refreshItemSpacing,
        hovered && !disabled && styles.refreshBtnHover,
        disabled && styles.refreshBtnDisabled,
      ]}>
      <Text style={styles.refreshBtnText}>
        {available ? `${left}/${max} Daily Refreshes` : 'Daily Refreshes Used'}
      </Text>
      {available && (
        <View style={styles.refreshCost}>
          <Text style={styles.refreshCostText}>{cost} QC</Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

/** "My Vault" chest; pulses when a purchased relic lands in the vault. */
const VaultChestButton = ({ pulse, isPhone, onPress }) => {
  const [hovered, setHovered] = useState(false);
  const t = useOneShot(pulse, 700);
  const stops = [0, 0.35, 0.65, 1];
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel="Open My Vault">
      <Animated.View
        style={[
          styles.chestBtn,
          isPhone ? styles.refreshItemPhone : styles.refreshItemSpacing,
          { transform: [{ scale: t.interpolate({ inputRange: stops, outputRange: [1, 1.22, 0.93, 1] }) }] },
          webOnly({
            backgroundImage:
              'radial-gradient(circle at 20px 19px, rgba(255,216,115,0.22), transparent 70%)',
            boxShadow: hovered
              ? '0 0 20px rgba(255,216,115,0.32), inset 0 0 10px rgba(255,216,115,0.12)'
              : '0 0 14px rgba(255,216,115,0.2), inset 0 0 10px rgba(255,216,115,0.08)',
          }),
        ]}>
        {/* chestPulse brightens the glow at its peak */}
        {IS_WEB && (
          <Animated.View
            pointerEvents="none"
            style={[
              StyleSheet.absoluteFill,
              styles.chestPulseGlow,
              {
                opacity: t.interpolate({ inputRange: stops, outputRange: [0, 1, 0.3, 0] }),
                boxShadow: '0 0 28px rgba(255,216,115,0.6), inset 0 0 16px rgba(255,216,115,0.25)',
              },
            ]}
          />
        )}
        <View
          style={[
            styles.chestIcon,
            IS_WEB
              ? {
                  backgroundImage:
                    'radial-gradient(circle, rgba(255,216,115,0.35), rgba(255,216,115,0.05) 72%)',
                }
              : styles.chestIconNative,
          ]}>
          <VaultChestIcon size={17} />
        </View>
        <Text style={styles.chestLabel}>My Vault</Text>
      </Animated.View>
    </TouchableOpacity>
  );
};

/** Sends the guardian away. Not in the handoff; added with the equip option. */
const NoGuardianButton = ({ active, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      disabled={active}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={active ? 'No guardian following you' : 'Send your guardian away'}
      style={[
        styles.noGuardianBtn,
        active && styles.noGuardianBtnActive,
        hovered && !active && styles.refreshBtnHover,
        hoverTransition(),
      ]}>
      <NoGuardianIcon color={active ? '#FFFFFF' : COLORS.lilacSoft} />
      <Text style={[styles.noGuardianText, active && { color: '#FFFFFF' }]}>
        {active ? 'No Guardian' : 'Go Without a Guardian'}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  content: {
    width: '100%',
    maxWidth: 1360,
    alignSelf: 'center',
    paddingTop: 40,
    paddingHorizontal: 32,
    paddingBottom: 80,
  },
  contentTablet: {
    paddingTop: 28,
    paddingHorizontal: 20,
    paddingBottom: 64,
  },
  contentPhone: {
    paddingTop: 20,
    paddingHorizontal: 14,
    paddingBottom: 56,
  },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 40,
  },
  topbarPhone: {
    marginBottom: 24,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 6,
  },
  // Logo takes its own row on phones, keeping the pinned close button clear of the pills.
  logoRowPhone: {
    width: '100%',
  },
  logoBadge: {
    width: 44,
    height: 44,
    marginRight: 12,
  },
  logoWord: {
    ...headingFont('800'),
    fontSize: 22,
    letterSpacing: em(0.01, 22),
    color: COLORS.textWarm,
  },
  logoWordPhone: {
    fontSize: 18,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 6,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 18,
  },
  pillPhone: {
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  pillSpacing: {
    marginLeft: 12,
  },
  coinBalance: {
    ...headingFont('700'),
    fontSize: 16,
    color: COLORS.gold,
    marginLeft: 8,
  },
  vaultPillText: {
    ...bodyFont('700'),
    fontSize: 13,
    color: COLORS.lilacSoft,
  },
  closeBtn: {
    marginLeft: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtnPinned: {
    position: 'absolute',
    top: 8,
    right: 0,
    marginLeft: 0,
  },
  hero: {
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 40,
  },
  heroPhone: {
    marginTop: 8,
    marginBottom: 24,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTitle: {
    ...headingFont('900'),
    fontSize: 34,
    letterSpacing: em(0.02, 34),
    textTransform: 'uppercase',
    color: COLORS.text,
    marginHorizontal: 16,
    textAlign: 'center',
  },
  heroTitleTablet: {
    fontSize: 28,
  },
  heroTitlePhone: {
    fontSize: 22,
  },
  heroSub: {
    ...bodyFont('600'),
    fontSize: 15,
    color: COLORS.lilac,
    marginTop: 10,
    textAlign: 'center',
  },
  panel: {
    position: 'relative',
    borderRadius: 26,
    paddingTop: 32,
    paddingHorizontal: 28,
    paddingBottom: 30,
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.30)',
    ...panelSurface,
  },
  panelPhone: {
    paddingTop: 22,
    paddingHorizontal: 16,
    paddingBottom: 20,
    borderRadius: 20,
  },
  vaultPanel: {
    overflow: 'hidden',
    borderColor: 'rgba(212,168,83,0.45)',
    ...webOnly({
      boxShadow:
        '0 0 0 1px rgba(212,168,83,0.12), inset 0 2px 0 rgba(255,224,158,0.14), inset 0 -4px 20px rgba(0,0,0,0.55), 0 20px 60px rgba(0,0,0,0.4)',
    }),
  },
  guardianPanel: {
    marginTop: 44,
    ...webOnly({
      boxShadow:
        '0 0 0 1px rgba(139,92,246,0.06), 0 20px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
    }),
  },
  panelTexture: {
    width: '100%',
    height: '100%',
  },
  panelTextureShade: {
    backgroundColor: 'rgba(10,8,6,0.26)',
  },
  corner: {
    position: 'absolute',
    width: 26,
    height: 26,
    opacity: 0.8,
  },
  panelHead: {
    alignItems: 'center',
    marginBottom: 26,
  },
  panelTitle: {
    ...headingFont('800'),
    fontSize: 22,
    letterSpacing: em(0.04, 22),
    textTransform: 'uppercase',
    color: COLORS.text,
    marginHorizontal: 12,
    textAlign: 'center',
  },
  panelSub: {
    ...bodyFont('600'),
    fontSize: 14,
    color: COLORS.lilac,
    marginTop: 8,
    textAlign: 'center',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  companionGridPhone: {
    width: '100%',
    maxWidth: 360,
    alignSelf: 'center',
  },
  refreshRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  refreshRowPhone: {
    flexDirection: 'column',
  },
  refreshItemSpacing: {
    marginLeft: 18,
  },
  refreshItemPhone: {
    marginTop: 12,
  },
  refreshNote: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  refreshNoteText: {
    ...bodyFont('600'),
    fontSize: 13.5,
    color: COLORS.lilac,
    marginLeft: 8,
  },
  refreshBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.5)',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 18,
  },
  refreshBtnHover: {
    backgroundColor: 'rgba(139,111,255,0.12)',
  },
  refreshBtnDisabled: {
    opacity: 0.4,
  },
  refreshBtnText: {
    ...bodyFont('700'),
    fontSize: 13,
    color: COLORS.lilacLight,
  },
  refreshCost: {
    marginLeft: 8,
    backgroundColor: 'rgba(255,216,115,0.14)',
    borderRadius: 999,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  refreshCostText: {
    ...bodyFont('800'),
    fontSize: 11,
    color: COLORS.gold,
  },
  chestBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 6,
    paddingRight: 16,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,216,115,0.5)',
  },
  chestPulseGlow: {
    borderRadius: 999,
  },
  chestIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chestIconNative: {
    backgroundColor: 'rgba(255,216,115,0.2)',
  },
  chestLabel: {
    ...headingFont('800'),
    fontSize: 13,
    letterSpacing: em(0.01, 13),
    color: '#FFE9B0',
    marginLeft: 9,
  },
  noGuardianBtn: {
    marginTop: 14,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.5)',
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  noGuardianBtnActive: {
    backgroundColor: COLORS.accent,
    borderColor: COLORS.accent,
  },
  noGuardianText: {
    ...bodyFont('700'),
    fontSize: 12.5,
    color: COLORS.lilacLight,
    marginLeft: 7,
  },
});

export default StoreView;
